import { useEffect, useMemo } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL } from "@/lib/format";
import { cn } from "@/lib/utils";
import { 
  ArrowLeft, Download, Box, Activity, TrendingUp, ShieldCheck, 
  Layers, Database, Zap, PieChart as PieIcon
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function InsightsPage() {
  const { dashboard } = useFiscalStore();
  const navigate = useNavigate();

  useEffect(() => { document.title = "Tax Metrics — DataGrid XML"; }, []);

  const { taxEntrada, taxSaida } = useMemo(() => {
    const cardsE = dashboard?.taxCardsE || [];
    const cardsS = dashboard?.taxCardsS || [];
    const totalE = cardsE.reduce((s, c) => s + c.totalValue, 0);
    const totalS = cardsS.reduce((s, c) => s + c.totalValue, 0);
    const revE = dashboard?.operacoesSummary?.entradas?.valorTotal || 0;
    const revS = dashboard?.operacoesSummary?.saidas?.valorTotal || 0;

    return {
      taxEntrada: { cards: cardsE, total: totalE, revenue: revE, carga: revE > 0 ? (totalE / revE) * 100 : 0 },
      taxSaida: { cards: cardsS, total: totalS, revenue: revS, carga: revS > 0 ? (totalS / revS) * 100 : 0 }
    };
  }, [dashboard]);

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-1000">
      
      {/* Header */}
      <header className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 border-l-4 border-primary pl-8 py-4 mb-10 bg-white/[0.02] glass-technical border-y-0 border-r-0 rounded-r-3xl">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Zap className="size-3 text-primary animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary">Tax Extraction Intelligence</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter leading-none mb-2">
            Métricas de <span className="text-primary italic">Impostos</span>
          </h1>
          <p className="text-[12px] text-muted-foreground font-black uppercase tracking-[0.1em] mt-2 flex items-center gap-6 opacity-70">
            <span className="flex items-center gap-2 uppercase tabular-nums"><Database className="size-3.5 text-primary" /> Extraction Engine v2.0</span>
            <span className="flex items-center gap-2 uppercase tabular-nums"><ShieldCheck className="size-3.5 text-emerald-500" /> Compliance Verified</span>
          </p>
        </div>
        <button onClick={() => navigate("/dashboard")} className="btn-glass h-12 px-6 flex items-center gap-2 rounded-2xl group">
          <ArrowLeft className="size-4 group-hover:-translate-x-1 transition-transform" /> 
          <span className="text-[10px] font-black uppercase tracking-widest">Voltar ao Painel</span>
        </button>
      </header>

      {/* Tax Matrix Overhaul */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <TaxBento title="Matriz de Extração (Entrada)" data={taxEntrada} color="emerald" icon={Download} />
        <TaxBento title="Matriz de Extração (Saída)" data={taxSaida} color="primary" icon={Box} />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard 
          label="Total ICMS Extraído" 
          value={fmtBRL((taxEntrada.cards.find(c => c.title === "ICMS")?.totalValue || 0) + (taxSaida.cards.find(c => c.title === "ICMS")?.totalValue || 0))} 
          sub="Créditos e Débitos Consolidados"
          icon={Layers}
        />
        <StatCard 
          label="Carga Tributária Média" 
          value={`${((taxEntrada.carga + taxSaida.carga) / 2).toFixed(2)}%`} 
          sub="Média Ponderada Sobre Volume"
          icon={TrendingUp}
        />
        <StatCard 
          label="Documentos Processados" 
          value={dashboard?.stats.totalNotas || 0} 
          sub="Total de XMLs Analisados"
          icon={PieIcon}
        />
      </div>
    </div>
  );
}

function TaxBento({ title, data, color, icon: Icon }: any) {
  return (
    <div className="glass-card p-10 group hover:border-primary/20 transition-all duration-700 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity animate-scan" />
      
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-8 mb-10">
        <div className="flex items-center gap-6">
          <div className={cn("size-16 rounded-3xl flex items-center justify-center shadow-2xl group-hover:rotate-12 transition-transform duration-700", 
            color === 'emerald' ? "bg-emerald-500/10 text-emerald-500 shadow-emerald-500/10" : "bg-primary/10 text-primary shadow-primary/10")}>
            <Icon className="size-8" />
          </div>
          <div>
            <h3 className="text-3xl font-black tracking-tighter leading-none mb-2">{title}</h3>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-muted-foreground opacity-60">Tax Extraction Intelligence Matrix</p>
          </div>
        </div>
        <div className="text-left sm:text-right">
          <div className={cn("text-4xl font-black tabular-nums tracking-tighter leading-none mb-2", color === 'emerald' ? "text-emerald-500" : "text-primary")}>
            {fmtBRL(data.total)}
          </div>
          <div className="flex items-center sm:justify-end gap-3">
            <div className="h-1.5 w-24 bg-muted/10 rounded-full overflow-hidden">
              <div className={cn("h-full relative", color === 'emerald' ? "bg-emerald-500" : "bg-primary")} style={{ width: `${data.carga}%` }}>
                 <div className="absolute inset-0 bg-white/20 animate-scan" />
              </div>
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest opacity-50">Carga {data.carga.toFixed(2)}%</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {data.cards.map((t: any) => (
          <div key={t.title} className="p-6 rounded-[2.5rem] glass-technical border-foreground/[0.03] hover:border-primary/20 transition-all group/card bg-white/[0.02] relative overflow-hidden">
            <div className="flex items-center justify-between mb-4 relative z-10">
              <span className="text-[10px] font-black uppercase tracking-widest opacity-40 group-hover/card:opacity-100 transition-opacity">{t.title}</span>
              <Activity className="size-3.5 opacity-20 group-hover/card:opacity-60 text-primary" />
            </div>
            <div className="text-2xl font-black tabular-nums tracking-tighter mb-6 relative z-10">{fmtBRL(t.totalValue)}</div>
            
            <div className="space-y-2 relative z-10">
               <div className="h-2 w-full bg-foreground/[0.03] rounded-full overflow-hidden border border-foreground/[0.01]">
                  <div 
                    className={cn("h-full transition-all duration-1000 relative", color === 'emerald' ? "bg-emerald-500" : "bg-primary")} 
                    style={{ width: `${data.total > 0 ? (t.totalValue / data.total) * 100 : 0}%` }} 
                  >
                    <div className="absolute inset-0 bg-white/20 animate-scan" />
                  </div>
               </div>
               <div className="flex justify-between items-center text-[9px] font-black text-muted-foreground uppercase tracking-widest opacity-60">
                  <span>Composition Share</span>
                  <span className="tabular-nums">{Math.round(data.total > 0 ? (t.totalValue / data.total) * 100 : 0)}%</span>
               </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon }: any) {
  return (
    <div className="glass-card p-6 border-foreground/5 hover:border-primary/20 transition-all group">
      <div className="flex items-center gap-4 mb-4">
        <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform">
          <Icon className="size-5" />
        </div>
        <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{label}</span>
      </div>
      <div className="text-3xl font-black tabular-nums tracking-tighter mb-1">{value}</div>
      <p className="text-[10px] font-bold text-muted-foreground/60 uppercase">{sub}</p>
    </div>
  );
}
